import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Copy, Check, Twitter, Sparkles, Filter } from 'lucide-react';
import { getClipboardNotes, ClipboardNote } from '../data/episodes';
import { getCategoryBadgeColor, getCategoryLabel } from '../utils/clipboardHelpers';

type Category = 'all' | 'users' | 'pfy' | 'management' | 'technical';

interface Note extends ClipboardNote {
  category: 'users' | 'pfy' | 'management' | 'technical';
}

const ClipboardPage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<Category>('all');
  const [copiedId, setCopiedId] = useState<number | null>(null);

  // Load notes from episodes
  const notes: Note[] = getClipboardNotes();

  // Calculate today's "Daily Wisdom" (deterministic based on date)
  const dailyWisdom = useMemo(() => {
    const today = new Date();
    const dayOfYear = Math.floor((today.getTime() - new Date(today.getFullYear(), 0, 0).getTime()) / 86400000);
    return notes[dayOfYear % notes.length];
  }, []);

  // Filter notes by category
  const filteredNotes = useMemo(() => {
    if (selectedCategory === 'all') {
      return notes;
    }
    return notes.filter(note => note.category === selectedCategory);
  }, [selectedCategory]);

  const copyToClipboard = async (note: Note) => {
    await navigator.clipboard.writeText(note.content);
    setCopiedId(note.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const tweetNote = (note: Note) => {
    const tweetText = `"${note.content}" — from The Operator's Clipboard`;
    const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`;
    window.open(tweetUrl, '_blank');
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Back button */}
      <div className="border-b border-gray-800">
        <div className="container mx-auto px-4 py-4">
          <Link
            to="/tools"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-red-500 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Tools
          </Link>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-6xl mx-auto">

          {/* Page Header */}
          <div className="mb-12 text-center">
            <div className="flex justify-center mb-6">
              <img
                src="/tool-icon-clipboard.png"
                alt="The Clipboard"
                className="w-24 h-24 object-contain"
              />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-100 mb-4">
              The Clipboard
            </h1>
            <p className="text-xl text-gray-400 mb-4">
              The Operator's personal notes and observations.
            </p>
            <p className="text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Every interaction documented. Every incident analyzed. Every excuse archived.
              This is where The Operator keeps the receipts.
            </p>
          </div>

          {/* Daily Wisdom Feature */}
          <div className="bg-gradient-to-br from-red-950/30 to-gray-900 border-2 border-red-500/50 rounded-lg p-8 mb-12 relative overflow-hidden">
            <div className="absolute top-4 right-4">
              <Sparkles className="w-8 h-8 text-red-500/30" />
            </div>
            <div className="flex items-start gap-4 mb-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-red-500/20 rounded-lg flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-red-500" />
                </div>
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-100 mb-2">
                  Daily Wisdom
                </h2>
                <p className="text-sm text-gray-400 mb-4">
                  Today's observation from The Operator's archives
                </p>
              </div>
            </div>
            <div className="bg-black/40 rounded-lg p-6 mb-4">
              <p className="text-gray-100 text-lg leading-relaxed">
                "{dailyWisdom.content}"
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold border ${getCategoryBadgeColor(dailyWisdom.category)}`}>
                {getCategoryLabel(dailyWisdom.category)}
              </span>
              <button
                onClick={() => copyToClipboard(dailyWisdom)}
                className="btn btn-secondary btn-sm flex items-center gap-2"
              >
                {copiedId === dailyWisdom.id ? (
                  <>
                    <Check className="w-4 h-4" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Copy
                  </>
                )}
              </button>
              <button
                onClick={() => tweetNote(dailyWisdom)}
                className="btn btn-secondary btn-sm flex items-center gap-2"
              >
                <Twitter className="w-4 h-4" />
                Tweet
              </button>
            </div>
          </div>

          {/* Category Filter */}
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-4">
              <Filter className="w-5 h-5 text-gray-400" />
              <h2 className="text-xl font-bold text-gray-100">Filter by Category</h2>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  selectedCategory === 'all'
                    ? 'bg-gray-100 text-black'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                All ({notes.length})
              </button>
              <button
                onClick={() => setSelectedCategory('users')}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  selectedCategory === 'users'
                    ? 'bg-red-500 text-white'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                User Observations ({notes.filter(n => n.category === 'users').length})
              </button>
              <button
                onClick={() => setSelectedCategory('pfy')}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  selectedCategory === 'pfy'
                    ? 'bg-teal-500 text-white'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                PFY Training ({notes.filter(n => n.category === 'pfy').length})
              </button>
              <button
                onClick={() => setSelectedCategory('management')}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  selectedCategory === 'management'
                    ? 'bg-amber-500 text-white'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                Management ({notes.filter(n => n.category === 'management').length})
              </button>
              <button
                onClick={() => setSelectedCategory('technical')}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  selectedCategory === 'technical'
                    ? 'bg-purple-500 text-white'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                Technical Wisdom ({notes.filter(n => n.category === 'technical').length})
              </button>
            </div>
          </div>

          {/* Notes Archive Grid */}
          <div className="grid md:grid-cols-2 gap-4 mb-12">
            {filteredNotes.map((note) => (
              <div
                key={note.id}
                className="bg-gray-900 border border-gray-800 rounded-lg p-6 hover:border-gray-700 transition-all"
              >
                <div className="flex items-start justify-between gap-4 mb-3">
                  <span className={`inline-block px-2 py-1 rounded-full text-xs font-bold border ${getCategoryBadgeColor(note.category)}`}>
                    {getCategoryLabel(note.category)}
                  </span>
                  <span className="text-xs text-gray-500 font-mono">#{note.id}</span>
                </div>
                <p className="text-gray-300 leading-relaxed mb-3">
                  {note.content}
                </p>
                <Link
                  to={`/episodes/${note.episodeSlug}`}
                  className="text-xs text-gray-500 hover:text-red-500 transition-colors mb-3 inline-block"
                >
                  From Episode {note.episodeNumber}: {note.episodeTitle}
                </Link>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => copyToClipboard(note)}
                    className="text-gray-400 hover:text-red-500 transition-colors p-2"
                    title="Copy to clipboard"
                  >
                    {copiedId === note.id ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </button>
                  <button
                    onClick={() => tweetNote(note)}
                    className="text-gray-400 hover:text-teal-500 transition-colors p-2"
                    title="Tweet this"
                  >
                    <Twitter className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Stats */}
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-6 mb-12">
            <h3 className="text-lg font-bold text-gray-100 mb-4">Archive Statistics</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-100">{notes.length}</div>
                <div className="text-sm text-gray-400">Total Notes</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-500">{notes.filter(n => n.category === 'users').length}</div>
                <div className="text-sm text-gray-400">User Obs.</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-teal-500">{notes.filter(n => n.category === 'pfy').length}</div>
                <div className="text-sm text-gray-400">PFY Notes</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-amber-500">{notes.filter(n => n.category === 'management').length}</div>
                <div className="text-sm text-gray-400">Mgmt.</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-500">{notes.filter(n => n.category === 'technical').length}</div>
                <div className="text-sm text-gray-400">Technical</div>
              </div>
            </div>
          </div>

          {/* Ethics Banner */}
          <div className="ethics-banner">
            <div className="text-center">
              <p className="text-sm text-gray-300 leading-relaxed">
                ⚠️ All observations are <strong>fictional and satirical</strong>.
                No actual users, PFYs, or management were harmed in the making of these notes.
                Any resemblance to real incidents is coincidental and hilarious.
              </p>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ClipboardPage;
